import React, { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { FaStore, FaUser, FaMotorcycle, FaTimes } from "react-icons/fa";
import "leaflet/dist/leaflet.css";

// --- Configuration ---
const ICON_CONFIG = {
  store: {
    icon: FaStore,
    mapIconUrl: "https://cdn-icons-png.flaticon.com/512/869/869636.png",
    color: "#60a5fa", // Blue
  },
  user: {
    icon: FaUser,
    mapIconUrl: "https://cdn-icons-png.flaticon.com/512/1077/1077063.png",
    color: "#4ade80", // Green
  },
  motorcycle: {
    icon: FaMotorcycle,
    mapIconUrl: "https://cdn-icons-png.flaticon.com/512/2972/2972185.png",
    color: "#fb923c", // Orange
  },
  default: {
    icon: FaMotorcycle,
    mapIconUrl: "https://cdn-icons-png.flaticon.com/512/2972/2972185.png",
    color: "#9ca3af", // Grey
  },
};

// --- Styles (Inline to ensure it works without Tailwind) ---
const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.75)",
    backdropFilter: "blur(4px)",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem",
  },
  modal: {
    backgroundColor: "#0f1724",
    width: "100%",
    maxWidth: "800px",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
    color: "white",
    display: "flex",
    flexDirection: "column",
    maxHeight: "90vh",
  },
  header: {
    padding: "16px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  closeBtn: {
    background: "transparent",
    border: "none",
    color: "#9ca3af",
    fontSize: "20px",
    cursor: "pointer",
    padding: "4px",
  },
  mapContainer: {
    height: "400px",
    width: "100%",
    position: "relative",
  },
  listContainer: {
    padding: "16px",
    backgroundColor: "#0f1724",
    overflowY: "auto",
    borderTop: "1px solid rgba(255,255,255,0.1)",
  },
  listItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    backgroundColor: "rgba(255,255,255,0.05)",
    padding: "12px",
    borderRadius: "8px",
    marginBottom: "8px",
  },
};

export function MapPopup({ markers = [], onClose }) {
  // 1. Safety check
  if (!markers || markers.length === 0) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div 
        style={styles.modal} 
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
      >
        {/* Header */}
        <div style={styles.header}>
          <h3 style={{ margin: 0, fontSize: "1.125rem", fontWeight: 600 }}>Location Map</h3>
          <button style={styles.closeBtn} onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Map */}
        <div style={styles.mapContainer}>
          <LeafletMap markers={markers} />
        </div>

        {/* List */}
        <div style={styles.listContainer}>
          {markers.map((m, idx) => {
            const config = ICON_CONFIG[m.type] || ICON_CONFIG.default;
            const IconComponent = config.icon;
            
            return (
              <div key={m.id || idx} style={styles.listItem}>
                <div style={{ 
                  padding: "8px", 
                  borderRadius: "6px", 
                  backgroundColor: "rgba(255,255,255,0.1)",
                  color: config.color,
                  display: "flex"
                }}>
                  <IconComponent size={16} />
                </div>
                <div>
                  <div style={{ fontWeight: 500 }}>{m.name}</div>
                  <div style={{ fontSize: "0.875rem", color: "#9ca3af" }}>{m.phone}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function LeafletMap({ markers }) {
  // Helper to create icon
  const createIcon = (type) => {
    const config = ICON_CONFIG[type] || ICON_CONFIG.default;
    return L.icon({
      iconUrl: config.mapIconUrl,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -40],
      shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
      shadowSize: [41, 41],
    });
  };

  // Safe center fallback
  const center = [markers[0].lat, markers[0].lng];

  return (
    <MapContainer
      center={center}
      zoom={13}
      style={{ height: "100%", width: "100%" }}
      scrollWheelZoom={true}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      <MapController markers={markers} />

      {markers.map((m, i) => (
        <Marker
          key={i}
          position={[m.lat, m.lng]}
          icon={createIcon(m.type)}
        >
          <Popup>
            <div style={{ textAlign: "center" }}>
              <strong>{m.name}</strong><br />
              {m.phone}
            </div>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
}

// This component handles the map "Refit" and "Resize" logic
function MapController({ markers }) {
  const map = useMap();

  useEffect(() => {
    if (!map || !markers.length) return;

    // We use a small timeout to allow the Modal CSS to finish animating
    // This fixes the "Grey Box" issue
    const timer = setTimeout(() => {
      map.invalidateSize(); // Force Leaflet to see the new container size

      const latlngs = markers.map((m) => [m.lat, m.lng]);
      const bounds = L.latLngBounds(latlngs);

      if (bounds.isValid()) {
        if (markers.length === 1) {
          map.setView(bounds.getCenter(), 14, { animate: false });
        } else {
          map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
        }
      }
    }, 200); // 200ms delay

    return () => clearTimeout(timer);
  }, [map, markers]);

  return null;
}